import { PII_PLACEHOLDER, scrubPiiForLlm } from "../pii-scrubber.js";
import { hasNegationOrDoubt } from "../resolvers/enum-slot.js";
import { findListLead, isUnaccentedText, nameTokensOf, type NameToken } from "../resolvers/name-text.js";
import { normalizeViText } from "../vi-normalizer.js";
import { listItemsOf } from "../resolvers/text-slot.js";

/**
 * 🔴 US-AIV2-0113 M01 (@UC0192 @UC0194) — LƯỚI TẤT ĐỊNH: danh sách NGƯỜI nói trong CÂU TẠO NHÓM.
 *
 * Đo 28/09 + ảnh TRƯỚC 01/10: *"tạo giúp anh nhóm đội bóng công ty tên FC Văn Phòng, gồm anh, Quân, Mai…"* ⇒ nhóm được tạo, còn danh sách người BỊ BỎ
 * LẶNG: trang `ONB-CREATE-GROUP` không có ô người, câu báo xong chỉ nói "bước tiếp theo thường là thêm thành viên".
 *
 * Lưới này KHÔNG ghi gì và KHÔNG điền ô nào của trang tạo nhóm. Nó chỉ ĐỌC chữ sau chữ dẫn đóng (`LIST_LEADS` — cùng nguồn với phép cắt đuôi tên
 * `cleanNameText`) để Người làm (api) làm hai việc sau khi nhóm ĐÃ tạo: NÓI LẠI những tên đã nghe, và đưa chúng vào nút "Thêm thành viên" (ô `danhSach`
 * của `ONB-ADD-MEMBERS`, qua `fill.danhSach`) — trang đó có resolver danh sách (tên trùng ⇒ hỏi), thẻ xác nhận LIỆT KÊ từng tên, và cổng nhóm phụ huynh
 * (nhóm thường ⇒ câu từ chối + nút màn Thêm thành viên / Link mời). Người dùng luôn thấy và bấm Đồng ý trước khi một suất nào được tạo.
 *
 * Luật (theo luật chung của `AI_DETERMINISTIC_NETS`):
 *  - **Chỉ chữ của câu** (đã cạo PII như mọi lưới): tên = mục của `listItemsOf` (cùng dấu tách với ô `list`), không sửa chính tả, không thêm dấu.
 *  - **Người nói không phải người được thêm**: mục đúng bằng đại từ ngôi 1 viết thường (`SELF_PRONOUNS`: "anh" · "chị" · "em" · "mình" · "tôi" · "tớ" ·
 *    "tao" · "tui") bị bỏ — người tạo nhóm đã là chủ nhóm. Viết HOA ("Anh") giữ: có thể là tên người; thẻ của ONB-ADD-MEMBERS liệt kê để sửa.
 *  - **Phủ định / câu hỏi / nửa chắc** (`hasNegationOrDoubt`) QUANH danh sách (review #1106 M1: đoạn sau chữ dẫn + ≤2 chữ thường ngay trước — tên nhóm
 *    "Hội Không Tên" không còn làm rơi danh sách) ⇒ `null`. Re-review L-b: chỉ xét chữ VIẾT THƯỜNG (+ từ đầu câu) — tên "Chàng"/"Chưa" không là phủ định.
 *  - Review #1106 H1 + re-review MEDIUM-1 — **số lượng không phải tên**: mục mở đầu bằng TIỀN TỐ ĐẾM (`countPrefixOf`: "30 bé" · "1.000 người" · "ba mươi bé" ·
 *    "khoảng 25 cháu lớp 3A") là lời đếm; mọi mục đều là đếm ⇒ `count` (Người làm nói lại đúng chữ đó, nút KHÔNG mang danh sách — suất "30 bé" là suất ảo
 *    chia tiền); trộn tên ⇒ `names` + `said` (L-a). Tiền tố + ":"/tên viết hoa ("2 bạn An", "2 bé: An") ⇒ bỏ tiền tố, giữ tên.
 *  - Review #1106 L1 — dấu cạo PII (`PII_PLACEHOLDER`) bị gỡ khỏi mục; mục chỉ còn dấu cạo ⇒ bỏ.
 *  - Không có chữ dẫn ⇒ `null` (không đoán từ dấu phẩy, "với", "có"). Có chữ dẫn mà không ra mục nào ⇒ `unreadable` (Người làm nói ra, mời dán danh sách —
 *    không bỏ lặng). Chỉ toàn đại từ người nói ⇒ `null` (không có ai để thêm).
 * Giới hạn đã biết: dấu "…"/"." cuối mục bị bỏ; mục kéo dài tới HẾT câu (người gõ thêm ý sau danh sách — "gồm Quân, Mai rồi mở đợt thu" — thì mục cuối
 * mang theo chữ thừa, thẻ ONB-ADD-MEMBERS in ra để sửa trước Đồng ý).
 */
export type MemberListFromSentence =
  /** Re-review #1106 L-a: `said` (chỉ khi có) = mục ĐẾM người gõ cùng câu ("gồm 30 bé và cô Lan" ⇒ ["30 bé"]) — để nói lại, không bao giờ thành suất. */
  | { readonly kind: "names"; readonly names: readonly string[]; readonly said?: readonly string[] }
  /** Review #1106 H1: mọi mục là SỐ LƯỢNG ("30 bé") — `said` = chữ người gõ, chỉ để NÓI LẠI; không bao giờ thành tên/suất. */
  | { readonly kind: "count"; readonly said: readonly string[] }
  | { readonly kind: "unreadable" }
  | null;

const SELF_PRONOUNS: ReadonlySet<string> = new Set(["anh", "chị", "em", "mình", "tôi", "tớ", "tao", "tui"]);
const ITEM_TRAIL_RE = /[\s.…]+$/u;
/**
 * Re-review #1106 MEDIUM-1 — TIỀN TỐ ĐẾM người (thay `COUNT_ITEM_RE` của H1, vốn đòi CẢ mục là "số + danh từ" ⇒ "30 bé lớp 3A" · "1.000 người" · "ba mươi bé" ·
 * "2 bạn An" lọt thành tên). Tiền tố = [ước lượng] + SỐ + DANH TỪ ĐẾM, so từng từ (`NameToken.flat`, bỏ dấu); mọi tập ĐÓNG:
 *  - SỐ: một từ chữ số, có dấu phân cách ("30" · "1.000"), hoặc dính danh từ ("30bé"); HOẶC chuỗi số ĐỌC CHỮ (`SPELLED_NUMBERS`) viết THƯỜNG, đúng dấu
 *    (gõ không dấu chỉ khi CẢ câu không dấu) — "Năm"/"Bảy" viết hoa là tên.
 *  - DANH TỪ ĐẾM (`COUNT_NOUNS`): "người", "bé", "bạn", "phụ huynh", "gia đình", "hộ"… "chàng" KHÔNG có ⇒ "3 Chàng Ngự Lâm" là tên.
 * Phần còn lại sau tiền tố (`countPrefixOf`):
 *  - rỗng / chỉ trợ từ ("nữa" · "thôi" · "à" · "nhé") ⇒ ĐẾM;
 *  - ":" ngay sau danh từ ("2 bé: An") ⇒ TÊN = phần sau dấu ":";
 *  - từ đầu là BỔ NGỮ chung (`COUNT_MODIFIERS`: lớp · của · trong · ở · nhà · khối…, bất kể hoa/thường — "30 bé Lớp 3A") ⇒ ĐẾM;
 *  - từ đầu VIẾT HOA ("2 bạn An") ⇒ TÊN = phần từ đó trở đi;
 *  - còn lại (chữ thường khác — "30 bé ngoan") = nghi ngờ ⇒ ĐẾM (an toàn: không tạo suất, câu mời dán tên).
 * Chữ số nằm TRONG tên ("Minh 2", "Tổ 4") không khớp: số phải đứng đầu mục.
 */
type Phrase = readonly string[];
const COUNT_ESTIMATORS: readonly Phrase[] = [["uoc", "luong"], ["uoc", "chung"], ["tong", "cong"], ["tat", "ca"], ["khoang"], ["tam"], ["chung"], ["gan"], ["hon"], ["tren"], ["do"], ["co"], ["tong"], ["ca"]];
/** Số đọc chữ — dạng CÓ DẤU (khoá: dạng bỏ dấu). */
const SPELLED_NUMBERS: ReadonlyMap<string, ReadonlySet<string>> = new Map(
  Object.entries({
    mot: ["một", "mốt"], hai: ["hai"], ba: ["ba"], bon: ["bốn"], tu: ["tư"], nam: ["năm"], lam: ["lăm"], nham: ["nhăm"], sau: ["sáu"], bay: ["bảy", "bẩy"],
    tam: ["tám"], chin: ["chín"], muoi: ["mười", "mươi"], tram: ["trăm"], nghin: ["nghìn"], ngan: ["ngàn"], trieu: ["triệu"], linh: ["linh"], le: ["lẻ"],
    chuc: ["chục"], vai: ["vài"], dam: ["dăm"],
  }).map(([k, v]) => [k, new Set(v)]),
);
const COUNT_NOUNS: readonly Phrase[] = [
  ["thanh", "vien"], ["hoc", "sinh"], ["phu", "huynh"], ["gia", "dinh"], ["dua", "tre"], ["anh", "chi"], ["anh", "em"], ["chi", "em"], ["nguoi", "ban"],
  ["nguoi"], ["ng"], ["be"], ["ban"], ["tv"], ["chau"], ["dua"], ["em"], ["hs"], ["ph"], ["nha"], ["ho"], ["tre"], ["con"], ["bac"], ["thang"], ["co"], ["chu"],
];
const COUNT_PARTICLES: ReadonlySet<string> = new Set(["nua", "thoi", "a", "nhe", "nha", "ha"]);
const COUNT_MODIFIERS: ReadonlySet<string> = new Set([
  "lop", "cua", "trong", "o", "nha", "khoi", "to", "doi", "nhom", "hoi", "phong", "cty", "congty", "cong", "thuoc", "ben", "xom", "khu", "truong", "hang", "cung", "tai", "va", "ke", "deu",
]);

/** Số đọc chữ: so NGUYÊN chữ gốc (không hạ chữ hoa) với dạng có dấu viết thường ⇒ "Năm"/"Bảy" viết hoa (tên) không khớp; gõ không dấu chỉ khi CẢ câu không dấu. */
function isSpelledNumber(t: NameToken, unaccented: boolean): boolean {
  const accented = SPELLED_NUMBERS.get(t.flat);
  if (accented === undefined) return false;
  const bare = t.raw.normalize("NFC").replace(/[^\p{L}\p{M}]+/gu, "");
  return accented.has(bare) || (unaccented && bare === t.flat);
}

function phraseAt(tokens: readonly NameToken[], at: number, phrases: readonly Phrase[]): number {
  for (const p of phrases) if (at + p.length <= tokens.length && p.every((w, j) => tokens[at + j]!.flat === w)) return p.length;
  return 0;
}

/** Số ở `at` (chữ số / chữ số dính danh từ / chuỗi số đọc chữ) ⇒ { next: token sau số, glued: danh từ dính sau chữ số }; không phải số ⇒ null. */
function numberAt(tokens: readonly NameToken[], at: number, unaccented: boolean): { readonly next: number; readonly glued: string } | null {
  const t = tokens[at];
  if (t === undefined) return null;
  const digits = /^(\d[\d.,]*)(\p{L}*)$/u.exec(t.raw.normalize("NFC").replace(/[:]+$/u, ""));
  if (digits !== null) return { next: at + 1, glued: normalizeViText(digits[2] ?? "") };
  let i = at;
  while (i < tokens.length && isSpelledNumber(tokens[i]!, unaccented)) i += 1;
  return i > at ? { next: i, glued: "" } : null;
}

/** Mục bắt đầu bằng tiền tố ĐẾM ⇒ `count` (cả mục là lời đếm) hoặc `name` (tên sau tiền tố); không có tiền tố ⇒ null. */
function countPrefixOf(item: string, unaccented: boolean): { readonly kind: "count" } | { readonly kind: "name"; readonly name: string } | null {
  const tokens = nameTokensOf(item);
  const est = phraseAt(tokens, 0, COUNT_ESTIMATORS);
  const num = (est > 0 ? numberAt(tokens, est, unaccented) : null) ?? numberAt(tokens, 0, unaccented);
  if (num === null) return null;
  let rest: number;
  if (num.glued !== "") {
    if (!COUNT_NOUNS.some((p) => p.length === 1 && p[0] === num.glued)) return null;
    rest = num.next;
  } else {
    const noun = phraseAt(tokens, num.next, COUNT_NOUNS);
    if (noun === 0) return null;
    rest = num.next + noun;
  }
  const nounTok = tokens[rest - 1]!;
  if (nounTok.raw.endsWith(":") || tokens[rest]?.raw.startsWith(":") === true) {
    const name = item.slice(item.indexOf(":", nounTok.start) + 1).trim();
    return name === "" ? { kind: "count" } : { kind: "name", name };
  }
  const tail = tokens.slice(rest);
  if (tail.every((t) => COUNT_PARTICLES.has(t.flat) || t.flat === "")) return { kind: "count" };
  const head = tail[0]!;
  if (COUNT_MODIFIERS.has(head.flat)) return { kind: "count" };
  if (/^\p{Lu}/u.test(head.raw.normalize("NFC"))) return { kind: "name", name: item.slice(head.start) };
  return { kind: "count" };
}

/** Số chữ trước chữ dẫn danh sách được xét phủ định/nghi ngờ ("không gồm", "chắc gồm", "chưa có ai gồm"). */
const NEGATION_WINDOW_BEFORE_LEAD = 2;
/** Review #1106 L1: dấu cạo PII (một nguồn — `PII_PLACEHOLDER`) không được lọt thành tên người ("Quân [SĐT]"). */
const PII_MARKERS: readonly string[] = Object.values(PII_PLACEHOLDER);

function withoutPiiMarkers(item: string): string {
  let out = item;
  for (const m of PII_MARKERS) out = out.split(m).join(" ");
  return out.replace(/\(\s*\)/gu, " ").replace(/\s+/gu, " ").trim();
}

/**
 * Review #1106 M1 — phủ định/nghi ngờ chỉ xét QUANH danh sách: các chữ thường ngay trước chữ dẫn (tối đa `NEGATION_WINDOW_BEFORE_LEAD`; chữ VIẾT HOA là
 * chữ của TÊN — "Hội Không Tên", "Chưa Đặt") + chữ dẫn + mọi chữ sau nó. Tên nhóm có "Không"/"Chưa" không còn làm rơi danh sách lặng. Trả chỉ số token đầu.
 */
function aroundListFrom(tokens: readonly NameToken[], at: number): number {
  let from = at;
  while (from > 0 && at - from < NEGATION_WINDOW_BEFORE_LEAD) {
    if (!isLowerOrFirst(tokens, from - 1)) break;
    from -= 1;
  }
  return from;
}

/** Chữ đầu của từ viết thường — hoặc là từ ĐẦU câu (bàn phím tự viết hoa "Không gồm…"). Từ không có chữ cái (dấu câu, số) ⇒ coi như thường. */
function isLowerOrFirst(tokens: readonly NameToken[], i: number): boolean {
  const first = /\p{L}/u.exec(tokens[i]!.raw)?.[0];
  return i === 0 || first === undefined || first === first.toLocaleLowerCase("vi");
}

/**
 * Re-review #1106 L-b — `hasNegationOrDoubt` (dùng chung, KHÔNG sửa) so từ BỎ DẤU ⇒ tên riêng "Chàng" (≡ "chẳng") · "Chưa" · "Đừng" trong danh sách làm cả
 * danh sách rơi LẶNG. Ở lưới này chỉ đưa cho nó các từ VIẾT THƯỜNG (và từ đầu câu) từ `from` trở đi; từ VIẾT HOA (tên riêng) chỉ giữ dấu câu của nó — "Mai?"
 * vẫn mang "?" ⇒ câu hỏi vẫn chặn.
 */
function lowercaseWordsOf(tokens: readonly NameToken[], from: number): string {
  const out: string[] = [];
  for (let i = from; i < tokens.length; i += 1) out.push(isLowerOrFirst(tokens, i) ? tokens[i]!.raw : tokens[i]!.raw.replace(/[\p{L}\p{M}\p{N}]+/gu, ""));
  return out.join(" ");
}

export function memberListFromSentence(sentence: string): MemberListFromSentence {
  const text = scrubPiiForLlm(sentence ?? "").text.normalize("NFC").trim();
  if (text === "") return null;
  const lead = findListLead(text);
  if (lead === null) return null;
  if (hasNegationOrDoubt(lowercaseWordsOf(lead.tokens, aroundListFrom(lead.tokens, lead.at)))) return null;
  const { tokens } = lead;
  const after = lead.at + lead.len < tokens.length ? text.slice(tokens[lead.at + lead.len]!.start) : "";
  const items = listItemsOf(after)
    .map((item) => withoutPiiMarkers(item).replace(ITEM_TRAIL_RE, ""))
    .filter((item) => item !== "");
  if (items.length === 0) return { kind: "unreadable" };
  const unaccented = isUnaccentedText(text);
  const names: string[] = [];
  const said: string[] = [];
  for (const item of items) {
    if (SELF_PRONOUNS.has(item)) continue;
    const counted = countPrefixOf(item, unaccented);
    if (counted === null) names.push(item);
    else if (counted.kind === "name") names.push(counted.name);
    else said.push(item);
  }
  if (names.length > 0) return { kind: "names", names, ...(said.length > 0 ? { said } : {}) };
  return said.length > 0 ? { kind: "count", said } : null;
}
